import { Controller } from "@hotwired/stimulus"

// Shop checkout (task 5b): Stripe Elements in deferred mode (no PaymentIntent until « Payer »).
// The server creates the intent at the click (POST paymentUrl) after re-checking the cart and the
// total the buyer saw; this controller then confirms it with Stripe.js, which redirects to the
// confirmation page. No optional chaining: the webpack/Babel setup does not transpile it.
export default class extends Controller {
  static values = {
    publishableKey: String,
    subtotalCents: Number,
    minChargeCents: Number,
    paymentMethodTypes: Array,
    pickupPaymentMethodTypes: Array,
    pickupAddress: Object,
    countries: Array,
    prefill: Object,
    giftCardsOnly: Boolean,
    noShipmentOption: String,
    shippingUrl: String,
    paymentUrl: String,
    lepotPaymentUrl: String,
    payLabel: String,
    freeLabel: String,
    genericError: String,
    throttledError: String
  }

  static targets = ["email", "address", "addressSection", "pickupSection", "pickupName", "pickupPhone", "shippingFrame", "express", "payment", "errors", "submit", "lepot", "shippingAmount", "total", "promoFrame"]

  connect() {
    this.shippingCents = null
    this.optionKey = null
    this.addressValue = null
    this.lastAddress = null
    this.pickup = false
    this.pickupShown = false
    this.email = null
    this.paying = false
    // Back from a refused payment: what the buyer typed (Shop::Confirmation#prefill). The option
    // is only a preference for the first quote; optionKey stays null until the frame offers it.
    this.preferredOptionKey = this.prefillValue.option_key || null
    this.stripe = Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements({
      mode: "payment",
      amount: this.subtotalCentsValue,
      currency: "eur",
      paymentMethodTypes: this.paymentMethodTypesValue,
      locale: "fr",
      // 16px at least: a smaller input makes iOS Safari zoom on focus.
      appearance: { theme: "flat", variables: { fontFamily: "'DM Sans', sans-serif", fontSizeBase: "16px", borderRadius: "8px" } }
    })

    // The e-mail is sent with the billing details at confirmation: settlement reads the buyer's
    // address from the charge (billing_details.email), the only one a guest gives us.
    this.email = this.prefillValue.email || null
    const auth = this.elements.create("linkAuthentication", this.email ? { defaultValues: { email: this.email } } : {})
    auth.mount(this.emailTarget)
    auth.on("change", (event) => { this.email = event.value.email || null })

    const address = this.elements.create("address", this.addressOptions())
    address.mount(this.addressTarget)
    address.on("change", (event) => this.addressChanged(event))
    // A prefilled address is read once the element is ready instead of waiting for a change
    // event: the quote and the « Payer » button must follow it without the buyer touching it.
    if (this.prefilledAddress()) {
      address.on("ready", () => address.getValue().then((event) => this.addressChanged(event)))
    }
    this.addressElement = address

    // The billing address comes from the shipping address (confirmPayment below): left to the
    // Payment Element, Klarna's own country picker defaulted to the visitor's first listed
    // country (AT) and Klarna refused the region (seen in test mode on 2026-10-01).
    const payment = this.elements.create("payment", {
      terms: { card: "never" },
      fields: { billingDetails: { address: "never" } }
    })
    payment.mount(this.paymentTarget)
    payment.on("change", () => this.clearError())

    // Klarna stays in the Payment Element: the Express Checkout Element would otherwise take it.
    // emailRequired: a guest paying with a wallet without typing the e-mail above still gives one
    // (settlement mails the address on the charge).
    this.express = this.elements.create("expressCheckout", {
      emailRequired: true,
      paymentMethods: { applePay: "auto", googlePay: "auto", link: "auto", klarna: "never", paypal: "never", amazonPay: "never" }
    })
    this.express.mount(this.expressTarget)
    this.express.on("confirm", (event) => this.pay(event))

    if (this.giftCardsOnlyValue) {
      // Nothing to quote: each card's delivery is in its price, the one option is « Sans envoi ».
      this.optionKey = this.noShipmentOptionValue
      this.shippingCents = 0
      this.updateTotal()
    } else if (this.prefillValue.pickup) {
      this.prefillPickup()
    }

    // Back from Stripe's redirect page restores this one from the browser cache, « paying » set.
    this.restored = (event) => { if (event.persisted) { this.paying = false; this.refreshPayability() } }
    window.addEventListener("pageshow", this.restored)
  }

  disconnect() {
    clearTimeout(this.quoteTimer)
    window.removeEventListener("pageshow", this.restored)
  }

  // Gift cards only (card 11): nothing ships to the buyer, so the address is the billing one
  // Klarna needs, from any country; a parcel needs the delivery address, its phone and a country
  // a shipping zone serves.
  addressOptions() {
    if (this.giftCardsOnlyValue) {
      return { mode: "billing", autocomplete: { mode: "automatic" }, defaultValues: this.addressDefaults() }
    }
    return {
      mode: "shipping",
      autocomplete: { mode: "automatic" },
      fields: { phone: "always" },
      validation: { phone: { required: "always" } },
      allowedCountries: this.countriesValue,
      defaultValues: this.addressDefaults()
    }
  }

  addressDefaults() {
    const fallback = { address: { country: this.countriesValue.indexOf("FR") >= 0 ? "FR" : this.countriesValue[0] } }
    if (!this.prefilledAddress()) return fallback

    const prefill = this.prefillValue
    return { name: prefill.name || "", phone: prefill.phone || "", address: prefill.address }
  }

  // Not for a pickup (its address is the shop's), nor in a country no longer served.
  prefilledAddress() {
    const prefill = this.prefillValue
    if (prefill.pickup || !prefill.address) return false
    return this.giftCardsOnlyValue || this.countriesValue.indexOf(prefill.address.country) >= 0
  }

  // A pickup comes back on the pickup option: the frame is quoted for the shop's address with
  // the pickup preferred, which swaps the address form for the name and phone (syncPickup).
  prefillPickup() {
    const prefill = this.prefillValue
    if (prefill.name) this.pickupNameTarget.value = prefill.name
    if (prefill.phone) this.pickupPhoneTarget.value = prefill.phone
    // The Address Element may report its default country on mount: that quote (no postcode, no
    // option) must not replace the pickup one before it lands. A complete address the buyer typed
    // is always quoted, so a pickup quote that never lands cannot block the page.
    this.holdAddressQuote = true
    this.loadShipping(this.pickupAddressValue)
  }

  // The shipping options depend on the country and the postcode (Corsica, overseas under FR).
  addressChanged(event) {
    this.clearError()
    this.addressValue = event.complete ? event.value : null
    this.lastAddress = event.value
    if (this.giftCardsOnlyValue) { this.refreshPayability(); return }
    if (this.holdAddressQuote && !event.complete) return
    const addr = event.value.address
    const key = `${addr.country}|${addr.postal_code || ""}`
    if (key === this.quotedFor) { this.refreshPayability(); return }

    this.quotedFor = key
    clearTimeout(this.quoteTimer)
    this.quoteTimer = setTimeout(() => this.loadShipping(addr), 300)
  }

  // `selected` is the buyer's current choice, so a re-quote keeps it checked when still offered.
  loadShipping(addr) {
    const params = new URLSearchParams({ country: addr.country, postcode: addr.postal_code || "", selected: this.optionKey || this.preferredOptionKey || "" })
    this.shippingFrameTarget.src = `${this.shippingUrlValue}?${params}`
  }

  // While a payment is in flight the option stays the one the intent was created for: the
  // recipient and the method list sent at confirmation must match it.
  shippingLoaded() {
    this.holdAddressQuote = false
    if (this.paying) return this.recheckOption()
    const checked = this.shippingFrameTarget.querySelector("input[name='shipping_option']:checked")
    if (checked) {
      this.applyShipping(checked)
    } else {
      this.optionKey = null
      this.shippingCents = null
      this.pickup = false
      this.syncPickup()
      this.shippingAmountTarget.textContent = "—"
      this.updateTotal()
    }
  }

  selectShipping(event) {
    if (this.paying) return this.recheckOption()
    this.applyShipping(event.target)
  }

  recheckOption() {
    this.shippingFrameTarget.querySelectorAll("input[name='shipping_option']").forEach((input) => {
      input.checked = input.value === this.optionKey
    })
  }

  applyShipping(input) {
    this.optionKey = input.value
    this.preferredOptionKey = null
    this.pickup = input.dataset.pickup === "true"
    this.syncPickup()
    this.shippingCents = parseInt(input.dataset.priceCents, 10)
    this.shippingAmountTarget.textContent = this.shippingCents === 0 ? this.freeLabelValue : this.formatCents(this.shippingCents)
    this.updateTotal()
  }

  // The promo frame came back (code applied or removed on this page): the items' subtotal moved,
  // so the total, Stripe's amount and the shipping options (a pickup is not offered on a 0 € order)
  // are rebuilt from it.
  promoChanged() {
    const holder = this.promoFrameTarget.querySelector("[data-payable-subtotal-cents]")
    if (!holder) return
    const subtotal = parseInt(holder.dataset.payableSubtotalCents, 10)
    if (isNaN(subtotal) || subtotal === this.subtotalCentsValue) return

    this.subtotalCentsValue = subtotal
    this.updateTotal()
    this.reloadShipping()
  }

  // Never under Stripe's minimum charge: a promo code that takes the items under it with a free
  // delivery leaves that much on the items at « Payer » (Shop::PromoDiscount), so the page shows it.
  totalCents() {
    return Math.max(this.subtotalCentsValue + (this.shippingCents || 0), this.minChargeCentsValue)
  }

  updateTotal() {
    const total = this.totalCents()
    this.totalTarget.textContent = this.formatCents(total)
    this.submitTarget.textContent = `${this.payLabelValue} ${this.formatCents(total)}`
    this.elements.update({ amount: total })
    this.refreshPayability()
  }

  // Pickup (Samuel's mockup): the Address Element leaves the page, only the name and phone of
  // whoever comes are asked. It is unmounted, not hidden: a mounted Address Element is validated by
  // elements.submit() and sent as the shipping address at confirmation. Klarna goes too: it needs
  // the buyer's billing address, which a pickup never asks for.
  syncPickup() {
    if (this.pickup === this.pickupShown) return
    this.pickupShown = this.pickup

    if (this.pickup) {
      this.addressElement.unmount()
      this.addressSectionTarget.hidden = true
      this.pickupSectionTarget.hidden = false
      this.prefillPickupContact()
      this.elements.update({ paymentMethodTypes: this.pickupPaymentMethodTypesValue })
    } else {
      this.pickupSectionTarget.hidden = true
      this.addressSectionTarget.hidden = false
      this.addressElement.mount(this.addressTarget)
      this.elements.update({ paymentMethodTypes: this.paymentMethodTypesValue })
    }
  }

  // What the buyer already typed in the address form saves typing it twice.
  prefillPickupContact() {
    const typed = this.lastAddress || {}
    if (this.pickupNameTarget.value.trim() === "" && typed.name) this.pickupNameTarget.value = typed.name
    if (this.pickupPhoneTarget.value.trim() === "" && typed.phone) this.pickupPhoneTarget.value = typed.phone
  }

  pickupContactChanged() {
    this.clearError()
    this.refreshPayability()
  }

  pickupContact() {
    return { name: this.pickupNameTarget.value.trim(), phone: this.pickupPhoneTarget.value.trim() }
  }

  ready() {
    if (this.optionKey === null) return false
    if (this.pickup) {
      const contact = this.pickupContact()
      return contact.name !== "" && contact.phone !== ""
    }
    return this.addressValue !== null
  }

  refreshPayability() {
    this.submitTarget.disabled = this.paying || !this.ready()
    if (this.hasLepotTarget) this.lepotTarget.disabled = this.paying || !this.ready()
    this.expressTarget.hidden = !this.ready()
  }

  // expressEvent: the Express Checkout confirm event (Apple Pay, Google Pay, Link), told when
  // the payment fails so its sheet closes instead of spinning until it times out.
  async pay(expressEvent) {
    this.expressEvent = expressEvent && expressEvent.paymentFailed ? expressEvent : null
    if (this.paying || !this.ready()) return this.failExpress()

    this.paying = true
    this.refreshPayability()
    this.clearError()
    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) return this.fail(submitError.message)

      const recipient = this.recipient()
      const pickup = this.pickup

      const response = await fetch(this.paymentUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          address: this.addressPayload(recipient),
          option_key: this.optionKey,
          expected_total_cents: this.totalCents(),
          // For the promo code's per-customer limit (a guest has only this e-mail).
          email: this.payerEmail(expressEvent)
        })
      })
      const body = await this.answer(response)
      if (body === null) return

      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: body.client_secret,
        confirmParams: this.confirmParams(body.return_url, recipient, pickup)
      })
      // Only reached when Stripe did not redirect (declined card, closed popup...).
      if (error) this.fail(error.message)
    } catch (err) {
      this.fail(this.genericErrorValue)
    }
  }

  // The e-mail typed above, else the one a wallet sheet gave (Apple Pay, Google Pay, Link).
  payerEmail(expressEvent) {
    if (this.email) return this.email
    if (expressEvent && expressEvent.billingDetails && expressEvent.billingDetails.email) return expressEvent.billingDetails.email
    return ""
  }

  // Le Pot Commun (coffrets and gift cards): the server opens a Le Pot order and the buyer pays
  // on Le Pot's page, which sends them back to the confirmation page. Le Pot gives us no e-mail:
  // the one typed above (or the signed-in account's, server side) is required.
  async payWithLepot() {
    if (this.paying || !this.ready()) return

    this.paying = true
    this.refreshPayability()
    this.clearError()
    try {
      const response = await fetch(this.lepotPaymentUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          address: this.addressPayload(this.recipient()),
          email: this.email || "",
          option_key: this.optionKey,
          expected_total_cents: this.totalCents()
        })
      })
      const body = await this.answer(response)
      if (body === null) return

      window.location.href = body.redirect_url
    } catch (err) {
      this.fail(this.genericErrorValue)
    }
  }

  // The server's answer to a payment click, or null once the refusal is handled (redirect,
  // reload, or the error shown).
  async answer(response) {
    // Rack::Attack answers 429 in plain text.
    if (response.status === 429) {
      this.fail(this.throttledErrorValue)
      return null
    }
    const body = await response.json()
    if (response.ok) return body

    if (body.redirect_url) {
      window.location.href = body.redirect_url
      return null
    }
    // The lines changed (another tab, a price update): the recap on this page is stale.
    if (body.subtotal_cents && body.subtotal_cents !== this.subtotalCentsValue) {
      window.location.reload()
      return null
    }
    // A moved total or a refused carrier: the options on screen are stale either way.
    this.reloadShipping()
    this.fail(body.error || this.genericErrorValue)
    return null
  }

  // A total that moved (shipping re-quoted, a price changed): show the server's options again,
  // keeping the carrier the buyer picked last (the frame's own src holds the one picked when the
  // address last changed).
  reloadShipping() {
    if (this.giftCardsOnlyValue) return
    if (this.pickup) {
      this.quotedFor = null
      this.loadShipping(this.pickupAddressValue)
      return
    }
    if (this.addressValue === null) return

    this.quotedFor = null
    this.loadShipping(this.addressValue.address)
  }

  // A pickup has no Address Element to send the shipping address: the shop's address and the
  // person who comes stand in, so the order records who collects it.
  confirmParams(returnUrl, recipient, pickup) {
    const params = { return_url: returnUrl, payment_method_data: { billing_details: this.billingDetails(recipient) } }
    if (pickup) params.shipping = { name: recipient.name, phone: recipient.phone, address: this.stripeAddress(recipient.address) }
    return params
  }

  // The name, phone and address the order ships to: the Address Element's, or the pickup's.
  recipient() {
    if (!this.pickup) return this.addressValue

    const contact = this.pickupContact()
    return { name: contact.name, phone: contact.phone, address: this.pickupAddressValue }
  }

  stripeAddress(addr) {
    return { line1: addr.line1, line2: addr.line2 || "", city: addr.city, postal_code: addr.postal_code, state: addr.state || "", country: addr.country }
  }

  billingDetails(value) {
    // Klarna needs the full billing address (country alone is refused); the shipping one stands in.
    const details = {
      name: value.name,
      address: this.stripeAddress(value.address)
    }
    // A billing address (gift cards only) has no phone.
    if (value.phone) details.phone = value.phone
    if (this.email) details.email = this.email
    return details
  }

  addressPayload(value) {
    const addr = value.address
    return {
      name: value.name,
      phone: value.phone || "",
      line1: addr.line1,
      line2: addr.line2,
      city: addr.city,
      postal_code: addr.postal_code,
      country: addr.country
    }
  }

  failExpress() {
    if (this.expressEvent) this.expressEvent.paymentFailed({ reason: "fail" })
    this.expressEvent = null
  }

  fail(message) {
    this.failExpress()
    this.paying = false
    this.errorsTarget.textContent = message
    this.refreshPayability()
  }

  clearError() {
    if (this.errorsTarget.textContent !== "") this.errorsTarget.textContent = ""
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  formatCents(cents) {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100)
  }
}
